import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Container, Stack, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { reportClientError } from '../lib/telemetry';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Catches synchronous React subtree failures so operators always see recoverable UI
 * instead of a blank shell.
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportClientError('admin.AppErrorBoundary', error, {
      componentStack: errorInfo.componentStack,
    });
    if (!import.meta.env.DEV) {
      console.error('[EvolveOS Admin] uncaught UI error:', error, errorInfo);
    }
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  private readonly handleHome = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.assign('/');
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Stack spacing={2} alignItems="center">
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h5" align="center">
              Something went wrong
            </Typography>
            <Alert severity="error" sx={{ width: '100%' }}>
              {this.state.error.message || 'An unexpected error occurred in this screen.'}
            </Alert>
            {import.meta.env.DEV && this.state.error.stack && (
              <Box
                component="pre"
                sx={{
                  width: '100%',
                  overflow: 'auto',
                  fontSize: 11,
                  p: 2,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  borderRadius: 1,
                }}
              >
                {this.state.error.stack}
              </Box>
            )}
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={this.handleReload}>
                Reload page
              </Button>
              <Button variant="outlined" onClick={this.handleHome}>
                Back to dashboard
              </Button>
            </Stack>
          </Stack>
        </Container>
      );
    }
    return this.props.children;
  }
}
